import { useState, useEffect, useMemo } from 'react';
import type { ApprovalPattern, PatternMatch, AutomationSettings } from '@/types/approval-automation';
import { ApprovalPatternService } from '@/services/approvalPatternService';
import type { Timesheet, Employee } from '@/types/timesheet';
import { toast } from 'sonner';

const DEFAULT_SETTINGS: AutomationSettings = {
  enabled: true,
  minConfidenceThreshold: 85,
  spotCheckPercentage: 10,
  maxAutoApprovalsPerDay: 100,
  notificationFrequency: 'weekly',
  requireConfirmation: true,
};

const STORAGE_KEY_PATTERNS = 'approval-automation-patterns';
const STORAGE_KEY_SETTINGS = 'approval-automation-settings';
const STORAGE_KEY_DISMISSED = 'approval-automation-dismissed';
const STORAGE_KEY_LAST_INSIGHT = 'approval-automation-last-insight';

export function useApprovalAutomation(
  timesheets: Timesheet[],
  employees: Employee[],
  currentUserId: number
) {
  const [patterns, setPatterns] = useState<ApprovalPattern[]>([]);
  const [settings, setSettings] = useState<AutomationSettings>(DEFAULT_SETTINGS);
  const [dismissedPatternIds, setDismissedPatternIds] = useState<Set<string>>(new Set());
  const [lastInsightShown, setLastInsightShown] = useState<Date | null>(null);
  const [showWeeklyInsight, setShowWeeklyInsight] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const storedPatterns = localStorage.getItem(STORAGE_KEY_PATTERNS);
      if (storedPatterns) {
        const parsed = JSON.parse(storedPatterns);
        // Convert date strings back to Date objects
        const patternsWithDates = parsed.map((p: any) => ({
          ...p,
          createdAt: new Date(p.createdAt),
          lastMatchedAt: p.lastMatchedAt ? new Date(p.lastMatchedAt) : undefined,
          enabledAt: p.enabledAt ? new Date(p.enabledAt) : undefined,
          dismissedAt: p.dismissedAt ? new Date(p.dismissedAt) : undefined,
        }));
        setPatterns(patternsWithDates);
      }

      const storedSettings = localStorage.getItem(STORAGE_KEY_SETTINGS);
      if (storedSettings) {
        setSettings(JSON.parse(storedSettings));
      }

      const storedDismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
      if (storedDismissed) {
        setDismissedPatternIds(new Set(JSON.parse(storedDismissed)));
      }

      const storedLastInsight = localStorage.getItem(STORAGE_KEY_LAST_INSIGHT);
      if (storedLastInsight) {
        setLastInsightShown(new Date(storedLastInsight));
      }
    } catch (error) {
      console.error('Error loading automation data:', error);
    }
  }, []);

  // Save to localStorage when patterns change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_PATTERNS, JSON.stringify(patterns));
    } catch (error) {
      console.error('Error saving patterns:', error);
    }
  }, [patterns]);

  // Save settings
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, [settings]);

  // Save dismissed patterns
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(Array.from(dismissedPatternIds)));
    } catch (error) {
      console.error('Error saving dismissed patterns:', error);
    }
  }, [dismissedPatternIds]);

  // Detect patterns from historical data
  const detectPatterns = () => {
    const approvedTimesheets = timesheets.filter(ts => ts.status === 'approved');
    const detectedPatterns = ApprovalPatternService.detectPatterns(
      approvedTimesheets,
      employees,
      currentUserId
    );

    // Merge with existing patterns, preserving status
    const existingPatternMap = new Map(patterns.map(p => [p.id, p]));
    const mergedPatterns = detectedPatterns.map(detected => {
      const existing = existingPatternMap.get(detected.id);
      if (existing) {
        // Update match count and confidence, but preserve status
        return {
          ...existing,
          matchCount: detected.matchCount,
          confidenceScore: detected.confidenceScore,
          confidence: detected.confidence,
          lastMatchedAt: detected.lastMatchedAt,
          potentialTimeSavings: detected.potentialTimeSavings,
        };
      }
      return detected;
    });

    setPatterns(mergedPatterns);
    return mergedPatterns;
  };

  // Check if weekly insight should be shown
  useEffect(() => {
    if (!lastInsightShown) {
      setShowWeeklyInsight(true);
      return;
    }

    const daysSinceLastInsight = Math.floor(
      (Date.now() - lastInsightShown.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceLastInsight >= 7) {
      setShowWeeklyInsight(true);
    }
  }, [lastInsightShown]);

  // Get enabled patterns
  const enabledPatterns = useMemo(
    () => patterns.filter(p => p.status === 'enabled'),
    [patterns]
  );

  // Get suggested patterns (not dismissed, not enabled)
  const suggestedPatterns = useMemo(
    () => patterns.filter(p => 
      p.status === 'suggested' && 
      !dismissedPatternIds.has(p.id) &&
      p.confidenceScore >= settings.minConfidenceThreshold
    ),
    [patterns, dismissedPatternIds, settings.minConfidenceThreshold]
  );

  // Get top suggested pattern for smart banner
  const topSuggestedPattern = useMemo(
    () => suggestedPatterns.sort((a, b) => b.confidenceScore - a.confidenceScore)[0] || null,
    [suggestedPatterns]
  );

  // Check if a timesheet matches any enabled patterns
  const checkTimesheetMatch = (timesheet: Timesheet): PatternMatch | null => {
    const employee = employees.find(e => e.id === timesheet.employeeId);
    if (!employee) return null;

    return ApprovalPatternService.matchTimesheet(timesheet, employee, enabledPatterns);
  };

  // Enable a pattern
  const enablePattern = (patternId: string) => {
    setPatterns(prev => prev.map(p => 
      p.id === patternId 
        ? { ...p, status: 'enabled' as const, enabledAt: new Date() }
        : p
    ));
    
    const pattern = patterns.find(p => p.id === patternId);
    if (pattern) {
      toast.success('Automation enabled', {
        description: `Future timesheets matching "${pattern.name}" will be flagged for auto-approval.`,
      });
    }
  };

  // Disable a pattern
  const disablePattern = (patternId: string) => {
    setPatterns(prev => prev.map(p => 
      p.id === patternId 
        ? { ...p, status: 'suggested' as const, enabledAt: undefined }
        : p
    ));
    
    toast.info('Automation disabled');
  };

  // Dismiss a pattern
  const dismissPattern = (patternId: string) => {
    setDismissedPatternIds(prev => new Set([...prev, patternId]));
    
    const pattern = patterns.find(p => p.id === patternId);
    if (pattern) {
      toast.info('Pattern dismissed', {
        description: 'You won\'t see this suggestion again.',
      });
    }
  };

  // Dismiss weekly insight card
  const dismissWeeklyInsight = () => {
    setShowWeeklyInsight(false);
    setLastInsightShown(new Date());
    localStorage.setItem(STORAGE_KEY_LAST_INSIGHT, new Date().toISOString());
  };

  // Update settings
  const updateSettings = (newSettings: Partial<AutomationSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  return {
    patterns,
    enabledPatterns,
    suggestedPatterns,
    topSuggestedPattern,
    settings,
    showWeeklyInsight,
    detectPatterns,
    checkTimesheetMatch,
    enablePattern,
    disablePattern,
    dismissPattern,
    dismissWeeklyInsight,
    updateSettings,
  };
}
